import React, { useEffect, useRef, useState } from 'react';
import styles from './EmojiPicker.module.css';

export interface EmojiOption {
  emoji: string;
  name: string;
}

export const FOOD_EMOJIS: EmojiOption[] = [
  { emoji: '🍔', name: 'burger' },
  { emoji: '🍕', name: 'pizza' },
  { emoji: '🌭', name: 'hot dog' },
  { emoji: '🌮', name: 'taco' },
  { emoji: '🌯', name: 'burrito' },
  { emoji: '🥙', name: 'wrap' },
  { emoji: '🥪', name: 'sandwich' },
  { emoji: '🍟', name: 'fries' },
  { emoji: '🍗', name: 'chicken' },
  { emoji: '🍖', name: 'meat' },
  { emoji: '🥩', name: 'steak' },
  { emoji: '🍤', name: 'shrimp' },
  { emoji: '🍣', name: 'sushi' },
  { emoji: '🍱', name: 'bento' },
  { emoji: '🍜', name: 'noodles' },
  { emoji: '🍝', name: 'pasta' },
  { emoji: '🍲', name: 'stew' },
  { emoji: '🥗', name: 'salad' },
  { emoji: '🥘', name: 'paella' },
  { emoji: '🧆', name: 'falafel' },
  { emoji: '🥡', name: 'takeout' },
  { emoji: '🍛', name: 'curry' },
  { emoji: '🍙', name: 'rice ball' },
  { emoji: '🍚', name: 'rice' },
  { emoji: '🥟', name: 'dumpling' },
  { emoji: '🥠', name: 'fortune cookie' },
  { emoji: '🍿', name: 'popcorn' },
  { emoji: '🧀', name: 'cheese' },
  { emoji: '🥓', name: 'bacon' },
  { emoji: '🥚', name: 'egg' },
  { emoji: '🍳', name: 'breakfast' },
  { emoji: '🥞', name: 'pancakes' },
  { emoji: '🧇', name: 'waffle' },
  { emoji: '🍩', name: 'donut' },
  { emoji: '🍪', name: 'cookie' },
  { emoji: '🎂', name: 'cake' },
  { emoji: '🍰', name: 'shortcake' },
  { emoji: '🧁', name: 'cupcake' },
  { emoji: '🍦', name: 'ice cream' },
  { emoji: '🍨', name: 'dessert' },
  { emoji: '🍧', name: 'shaved ice' },
  { emoji: '🍺', name: 'beer' },
  { emoji: '🍻', name: 'beers' },
  { emoji: '🍷', name: 'wine' },
  { emoji: '🍸', name: 'cocktail' },
  { emoji: '🍹', name: 'tropical drink' },
  { emoji: '🥤', name: 'soda' },
  { emoji: '☕', name: 'coffee' },
  { emoji: '🧃', name: 'juice' },
  { emoji: '🍽️', name: 'dining' },
  { emoji: '🥂', name: 'champagne' },
  { emoji: '🌶️', name: 'spicy' },
  { emoji: '🥑', name: 'avocado' },
  { emoji: '🍓', name: 'strawberry' },
  { emoji: '🍋', name: 'lemon' },
  { emoji: '🥜', name: 'peanuts' },
  { emoji: '🍫', name: 'chocolate' },
];

export interface EmojiPickerProps {
  value: string;
  onPick: (emoji: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ value, onPick, onClose, anchorRef }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [custom, setCustom] = useState('');

  const filtered = FOOD_EMOJIS.filter(
    (item) =>
      item.name.includes(search.toLowerCase()) ||
      item.emoji.includes(search),
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorRef, onClose]);

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = custom.trim();
    if (trimmed) {
      onPick(trimmed.slice(0, 4));
      onClose();
    }
  };

  return (
    <div ref={popoverRef} className={styles.popover} role="dialog" aria-label="Pick emoji">
      <input
        type="search"
        className={styles.search}
        placeholder="Search emojis…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <div className={styles.grid}>
        {filtered.map((item) => (
          <button
            key={`${item.emoji}-${item.name}`}
            type="button"
            className={`${styles.emojiButton} ${value === item.emoji ? styles.emojiButtonActive : ''}`}
            onClick={() => {
              onPick(item.emoji);
              onClose();
            }}
            title={item.name}
            aria-label={item.name}
          >
            {item.emoji}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className={styles.noResults}>No emojis match your search</p>
        )}
      </div>
      <form className={styles.customForm} onSubmit={handleCustomSubmit}>
        <input
          type="text"
          className={styles.customInput}
          placeholder="Custom emoji or text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          maxLength={4}
        />
        <button type="submit" className={styles.customSubmit}>
          Use
        </button>
      </form>
    </div>
  );
};

export default EmojiPicker;
